// Expert games, part 1: managing open trades, reading structure under
// pressure, and the engine's real signals. Every answer comes from the
// candles up to "now" (or the scenario's facts) and the desk's rules in
// ../rules.ts — never from what price did next.
import type { Candle, ChartMark, GameData, GameDef, Option, Rng } from "../types";
import type { ChartSignal } from "../../chart/chartSignals";
import { RESULT_META } from "../../chart/chartSignals";
import { emaSeries } from "../../chart/indicators";
import { pricePrecision } from "../../desk/deskData";
import {
  atr, biasCheck, chartOf, cutAt, dateText, emaLast, pctChange, pctText, planOutcome, price, readStructure, rOf, roundTo, rText, swings, usd,
  type BiasCheck, type Swing,
} from "../helpers";
import {
  body, isRejectionWick, lowerWick, MAX_DAILY_LOSS_R, MAX_TRADES_PER_DAY, pullbackStop, reactionTrigger, RISK_PCT, TIME_STOP_HOURS,
  TP1_FALLBACK_R, TRIGGER_WORDS, triggerWords, upperWick, MIN_ROOM_R, TP2_R,
} from "../rules";

// ── Small shared bits ───────────────────────────────────────────────────────

type Side = "LONG" | "SHORT";
const sideOf = (isLong: boolean): Side => (isLong ? "LONG" : "SHORT");
/** A price rounded the way the desk shows it, so the maths uses what the player sees. */
const snap = (n: number): number => roundTo(n, pricePrecision(n));
const hoursText = (h: number): string => `${h} hour${h === 1 ? "" : "s"}`;

/** A price distance (a wick, a risk) at the decimals of the price it was measured on: "0.45", "120.53". */
function diffText(d: number, ref: number): string {
  const dp = pricePrecision(ref);
  const a = Math.abs(d);
  return a >= 1000 ? a.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp }) : a.toFixed(dp);
}

/** The reveal for a moment cut inside the history: the candles after index `i`, then the data's own future. */
function after(data: GameData, i: number, n: number): Candle[] {
  return data.candles.slice(i + 1).concat(data.ahead).slice(0, n);
}

/** "Over the next 12 candles price went from 142.10 to 139.80 (−1.62%)." */
function moveText(from: number, ahead: Candle[]): string | undefined {
  const last = ahead[ahead.length - 1];
  if (!last) return undefined;
  const pct = pctChange(from, last.close);
  return `Over the next ${ahead.length} candles price went from ${price(from)} to ${price(last.close)} (${pct > 0 ? "+" : ""}${pctText(pct)}).`;
}

/** What a signal did, in words (only ever used as an outcome). */
function signalResultText(s: ChartSignal): string {
  return `${RESULT_META[s.result].plain}${s.r === null ? "" : ` Result: ${rText(s.r)}.`}`;
}

function setupName(archetype: string | null): string {
  if (archetype === "trend_pullback") return "trend pullback";
  if (archetype === "range_fade") return "range fade";
  if (archetype === "breakout_retest") return "breakout retest";
  return "setup";
}

const COINS = [
  { coin: "BTC", px: 64000 },
  { coin: "ETH", px: 3200 },
  { coin: "SOL", px: 145 },
  { coin: "AVAX", px: 28 },
  { coin: "LINK", px: 14 },
  { coin: "XRP", px: 0.62 },
  { coin: "DOGE", px: 0.18 },
] as const;

/** A random coin, side, entry and stop `stopPct` % away (all on the desk's price grid). */
function randomPlan(rng: Rng, stopPct: [number, number]) {
  const c = rng.pick(COINS);
  const isLong = rng.chance(0.5);
  const entry = snap(c.px * rng.float(0.9, 1.1));
  const pct = rng.float(stopPct[0], stopPct[1]);
  const stop = snap(entry * (1 + (isLong ? -1 : 1) * pct / 100));
  return { coin: c.coin, isLong, entry, stop, risk: Math.abs(entry - stop) };
}

// ── 1. Hold or fold? ────────────────────────────────────────────────────────

export type HoldCall = "take-half" | "time-stop" | "hold";
/** Within this many R of the stop or target 1 is too close to call by eye. */
const NEAR_R = 0.05;

/**
 * The plan's call for an open trade at the last of `since` (the candles after
 * the entry candle, one per hour): target 1 reached on the last candle →
 * take half off; 24 hours gone → the time stop; else hold. Null when the stop
 * was hit (or nearly), target 1 was reached earlier, exactly 24 hours have
 * passed, or a candle came within a hair of a level.
 */
export function holdOrFoldCall(isLong: boolean, entry: number, stop: number, tp1: number, since: Candle[]): HoldCall | null {
  const risk = Math.abs(entry - stop);
  const hours = since.length;
  if (!(risk > 0) || hours < 3 || hours > 30 || hours === TIME_STOP_HOURS) return null;
  const m = NEAR_R * risk;
  for (let k = 0; k < hours; k++) {
    const c = since[k]!;
    const pastStop = isLong ? stop - c.low : c.high - stop;
    if (pastStop > -m) return null;
    const pastTp = isLong ? c.high - tp1 : tp1 - c.low;
    const isLast = k === hours - 1;
    if (!isLast || hours > TIME_STOP_HOURS) {
      if (pastTp > -m) return null;
    } else {
      if (Math.abs(pastTp) < m) return null;
      if (pastTp > 0) return "take-half";
    }
  }
  return hours > TIME_STOP_HOURS ? "time-stop" : "hold";
}

export const holdOrFold: GameDef = {
  id: "hold-or-fold",
  level: "expert",
  title: "Hold or fold?",
  skill: "Manage an open trade by its plan: take half at target 1, close at the time stop, otherwise hold — never widen the stop.",
  needs: { tf: "1h", history: 200, ahead: 24 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const n = c.length;
    const closes = c.map(k => k.close);
    const e20 = emaSeries(closes, 20);
    const e50 = emaSeries(closes, 50);
    type Plan = { i: number; isLong: boolean; entry: number; stop: number; tp1: number };
    // The engine's plan for an entry at the close of each candle i (direction from the EMA order there).
    const plans = new Map<number, Plan>();
    for (let i = Math.max(60, n - 71); i <= n - 4; i++) {
      const a20 = e20[i];
      const a50 = e50[i];
      if (a20 == null || a50 == null || a20 === a50) continue;
      const isLong = a20 > a50;
      const upTo = c.slice(0, i + 1);
      const a = atr(upTo);
      if (!a || a <= 0) continue;
      const entry = c[i]!.close;
      const stop = pullbackStop(upTo, isLong, a);
      const risk = Math.abs(entry - stop);
      if (!(risk > 0)) continue;
      plans.set(i, { i, isLong, entry, stop, tp1: entry + (isLong ? 1 : -1) * TP1_FALLBACK_R * risk });
    }
    // "Now" is candle k (the last shown), 3–30 hours after the entry.
    type Trade = Plan & { k: number };
    const found: Record<HoldCall, Trade[]> = { "take-half": [], "time-stop": [], hold: [] };
    for (let k = n - 1; k >= Math.max(70, n - 40); k--) {
      for (let i = k - 30; i <= k - 3; i++) {
        const p = plans.get(i);
        if (!p) continue;
        const call = holdOrFoldCall(p.isLong, p.entry, p.stop, p.tp1, c.slice(i + 1, k + 1));
        if (call) found[call].push({ ...p, k });
      }
    }
    const kinds = (Object.keys(found) as HoldCall[]).filter(k => found[k].length > 0);
    if (kinds.length === 0) return null;
    const call = rng.pick(kinds);
    const t = rng.pick(found[call]);
    const side = sideOf(t.isLong);
    const hours = t.k - t.i;
    const now = c[t.k]!.close;
    const shown = c.slice(0, t.k + 1);
    const ahead = after(data, t.k, 24);
    const rNow = rOf(side, t.entry, t.stop, now);
    const verb = t.isLong ? "bought" : "sold";

    const options: Option[] = [
      {
        id: "take-half",
        label: "Take half off and move the stop to the entry",
        why: call === "take-half" ? undefined : `No candle has reached target 1 (${price(t.tp1)}) yet, so there's nothing to take.`,
      },
      {
        id: "time-stop",
        label: `Close it: the ${TIME_STOP_HOURS}-hour time stop`,
        why: call === "time-stop" ? undefined : `Only ${hoursText(hours)} have passed: the time stop is at ${TIME_STOP_HOURS}.`,
      },
      {
        id: "hold",
        label: "Hold: nothing in the plan has happened yet",
        why: call === "hold"
          ? undefined
          : call === "take-half"
            ? `Something did happen: the last candle reached target 1 (${price(t.tp1)}).`
            : `Something did happen: ${hoursText(hours)} passed without target 1, past the ${TIME_STOP_HOURS}-hour time stop.`,
      },
      {
        id: "widen",
        label: "Widen the stop to give it room",
        why: "Never. Moving the stop further away turns a planned 1R loss into a bigger one. A stop only moves toward profit (to the entry after target 1).",
      },
    ];

    const explain =
      call === "take-half"
        ? `The last candle reached target 1 (${price(t.tp1)}, +${TP1_FALLBACK_R}R). The plan: take half off there and move the stop to the entry (${price(t.entry)}), so the rest of the trade can no longer lose.`
        : call === "time-stop"
          ? `${hoursText(hours)} have passed and target 1 (${price(t.tp1)}) was never reached. The plan closes a trade that hasn't worked in ${TIME_STOP_HOURS} hours (here at ${rText(rNow)}): the idea has had its time.`
          : `After ${hoursText(hours)} price is at ${price(now)} (${rText(rNow)}, R = the risk you planned). It hasn't reached target 1 (${price(t.tp1)}) or the stop (${price(t.stop)}), and the time stop is ${hoursText(TIME_STOP_HOURS - hours)} away. The plan says hold.`;

    const outcome = call === "hold"
      ? planOutcome(side, t.entry, t.stop, t.tp1, ahead) || undefined
      : moveText(now, ahead);

    return {
      kind: "choice",
      prompt: `You ${verb} at the close of the candle marked E, ${hoursText(hours)} ago (stop and target 1 drawn). What does your plan say to do now?`,
      facts: [
        { label: "Side", value: t.isLong ? "Buy" : "Sell" },
        { label: "Entry", value: price(t.entry) },
        { label: "Stop", value: price(t.stop) },
        { label: `Target 1 (${TP1_FALLBACK_R}R)`, value: price(t.tp1) },
        { label: "In the trade", value: hoursText(hours) },
        { label: "Time stop", value: `${TIME_STOP_HOURS} hours` },
      ],
      chart: {
        tf: data.tf,
        candles: shown,
        ahead,
        focus: 60,
        emas: [20, 50],
        lines: [
          { price: t.entry, label: "Entry", tone: "brand" },
          { price: t.stop, label: "Stop", tone: "loss" },
          { price: t.tp1, label: "Target 1", tone: "profit" },
        ],
        marks: [{ time: c[t.i]!.time, label: "E", tone: "brand", position: t.isLong ? "below" : "above" }],
      },
      options: rng.shuffle(options),
      answer: call,
      explain,
      outcome,
    };
  },
};

// ── 2. Range-floor wick ─────────────────────────────────────────────────────

export type WickCall = "yes" | "short" | "outside";
/** Candles the range is read over (backend detectRangeFade: the last 30 bars). */
const RANGE_BARS = 30;
/** The rejection-wick ratio (rules.ts isRejectionWick). */
const WICK_RATIO = 1.2;

export interface RangeEdges { floor: number; ceiling: number; atr: number }

/**
 * The range before candle `i`: the lowest low and highest high of the 30
 * candles before it, kept only if it passes the classifier's checks (at
 * least 2 touches of each edge within 0.5 ATR, and at least 1.5 ATR tall).
 */
export function rangeBefore(candles: Candle[], i: number): RangeEdges | null {
  if (i < RANGE_BARS + 1 || i >= candles.length) return null;
  const win = candles.slice(i - RANGE_BARS, i);
  const a = atr(candles.slice(0, i + 1));
  if (!a || a <= 0) return null;
  const floor = Math.min(...win.map(k => k.low));
  const ceiling = Math.max(...win.map(k => k.high));
  if (ceiling - floor < 1.5 * a) return null;
  const highTouches = win.filter(k => k.high >= ceiling - 0.5 * a).length;
  const lowTouches = win.filter(k => k.low <= floor + 0.5 * a).length;
  if (highTouches < 2 || lowTouches < 2) return null;
  return { floor, ceiling, atr: a };
}

/** The wick against the edge ÷ the body (Infinity for a candle with no body). */
export function wickRatio(c: Candle, isLong: boolean): number {
  const b = body(c);
  const w = isLong ? lowerWick(c) : upperWick(c);
  return b > 0 ? w / b : w > 0 ? Infinity : 0;
}

/**
 * A candle at the floor (isLong) or ceiling: closed outside the range → a
 * breakdown; else the trigger if its wick against the edge is over 1.2× the
 * body. Null for a close within 0.05 ATR of the edge or a ratio within 0.1
 * of 1.2.
 */
export function rangeWickCall(c: Candle, isLong: boolean, r: RangeEdges): WickCall | null {
  const past = isLong ? r.floor - c.close : c.close - r.ceiling;
  if (Math.abs(past) < 0.05 * r.atr) return null;
  if (past > 0) return "outside";
  if (Math.abs(wickRatio(c, isLong) - WICK_RATIO) < 0.1) return null;
  return isRejectionWick(c, isLong) ? "yes" : "short";
}

const ratioText = (x: number): string => (x >= 10 ? "over 10" : x.toFixed(1));

export const rangeFloorWick: GameDef = {
  id: "range-floor-wick",
  level: "expert",
  title: "Range-floor wick",
  skill: "Spot the range-fade trigger: a candle at the edge whose wick against it is longer than 1.2× its body, closing back inside.",
  needs: { tf: "1h", history: 160, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    type Moment = { i: number; isLong: boolean; r: RangeEdges };
    const found: Record<WickCall, Moment[]> = { yes: [], short: [], outside: [] };
    for (let i = RANGE_BARS + 15; i < c.length; i++) {
      const r = rangeBefore(c, i);
      if (!r) continue;
      const k = c[i]!;
      const atFloor = k.low <= r.floor + 0.15 * r.atr;
      const atCeiling = k.high >= r.ceiling - 0.15 * r.atr;
      if (atFloor === atCeiling) continue;
      const call = rangeWickCall(k, atFloor, r);
      if (call) found[call].push({ i, isLong: atFloor, r });
    }
    const kinds = (Object.keys(found) as WickCall[]).filter(k => found[k].length > 0);
    if (kinds.length === 0) return null;
    const call = rng.pick(kinds);
    const { i, isLong, r } = rng.pick(found[call]);
    const k = c[i]!;
    const edge = isLong ? r.floor : r.ceiling;
    const edgeWord = isLong ? "floor" : "ceiling";
    const wickWord = isLong ? "lower" : "upper";
    const outWord = isLong ? "below" : "above";
    const inWord = isLong ? "above" : "below";
    const w = isLong ? lowerWick(k) : upperWick(k);
    const ratio = wickRatio(k, isLong);
    const shown = c.slice(0, i + 1);
    const ahead = after(data, i, 12);

    const options: Option[] = [
      {
        id: "yes",
        label: isLong ? "Yes: a buy trigger at the floor" : "Yes: a sell trigger at the ceiling",
        why: call === "short"
          ? `The ${wickWord} wick is only ${ratioText(ratio)}× the body; the trigger needs more than ${WICK_RATIO}×.`
          : call === "outside"
            ? `It closed ${outWord} the ${edgeWord} (${price(edge)}). A fade needs the candle to close back inside the range.`
            : undefined,
      },
      {
        id: "short",
        label: "No: the wick is too short",
        why: call === "yes"
          ? `The ${wickWord} wick is ${ratioText(ratio)}× the body: more than the ${WICK_RATIO}× the trigger needs.`
          : call === "outside"
            ? `The wick isn't the problem: the close (${price(k.close)}) is ${outWord} the ${edgeWord}.`
            : undefined,
      },
      {
        id: "outside",
        label: isLong ? "No: it closed outside the range (a breakdown, not a bounce)" : "No: it closed outside the range (a breakout, not a rejection)",
        why: call === "outside" ? undefined : `It closed at ${price(k.close)}, back inside the range (${inWord} the ${edgeWord} at ${price(edge)}).`,
      },
    ];

    const rule = `The range-fade trigger is a candle at the edge whose ${wickWord} wick (the side against the edge) is longer than ${WICK_RATIO}× its body.`;
    const explain =
      call === "outside"
        ? `It closed at ${price(k.close)}, ${outWord} the ${edgeWord} (${price(edge)}): the range broke. A fade ${isLong ? "buys a bounce" : "sells a rejection"} back inside the range, never a close outside it.`
        : call === "yes"
          ? `${rule} Here the wick is ${diffText(w, k.close)} and the body ${diffText(body(k), k.close)}: ${ratioText(ratio)}× the body, closing back inside. Price was pushed ${isLong ? "down to the floor and bought back up" : "up to the ceiling and sold back down"}.`
          : `${rule} Here the wick is ${diffText(w, k.close)} and the body ${diffText(body(k), k.close)}: only ${ratioText(ratio)}×. Not enough rejection: no trigger yet.`;

    const mid = (r.floor + r.ceiling) / 2;
    const fadeStop = isLong ? r.floor - 0.5 * r.atr : r.ceiling + 0.5 * r.atr;
    const outcome = call === "yes"
      ? `A ${isLong ? "buy" : "sell"} at the close (stop 0.5 ATR past the ${edgeWord}, target the middle): ${planOutcome(sideOf(isLong), k.close, fadeStop, mid, ahead)}`
      : moveText(k.close, ahead);

    return {
      kind: "choice",
      prompt: `Price has been moving between a floor and a ceiling (the lines). The marked candle just touched the ${edgeWord}. Is it the range-fade ${isLong ? "buy" : "sell"} trigger?`,
      facts: [
        { label: "Open", value: price(k.open) },
        { label: "High", value: price(k.high) },
        { label: "Low", value: price(k.low) },
        { label: "Close", value: price(k.close) },
      ],
      chart: {
        tf: data.tf,
        candles: shown,
        ahead,
        focus: 45,
        lines: [
          { price: r.ceiling, label: "Ceiling", tone: "loss", dashed: true },
          { price: r.floor, label: "Floor", tone: "profit", dashed: true },
        ],
        marks: [{ time: k.time, label: "?", tone: "brand", position: isLong ? "below" : "above" }],
      },
      options,
      answer: call,
      explain,
      outcome,
    };
  },
};

// ── 3. Is the trend broken? ─────────────────────────────────────────────────

export interface BrokenRead { trend: "up" | "down"; broken: boolean; pivot: Swing; all: Swing[]; atr: number }

/** The structure at the last close (swings + readStructure); null in a range or within 0.1 ATR of the pivot. */
export function trendBrokenRead(candles: Candle[]): BrokenRead | null {
  const last = candles[candles.length - 1];
  const a = atr(candles);
  if (!last || !a || a <= 0) return null;
  const all = swings(candles);
  const read = readStructure(all, last.close);
  if (read.trend === "range" || !read.pivot) return null;
  if (Math.abs(last.close - read.pivot.price) < 0.1 * a) return null;
  return { trend: read.trend, broken: read.broken, pivot: read.pivot, all, atr: a };
}

export const trendBroken: GameDef = {
  id: "trend-broken",
  level: "expert",
  title: "Is the trend broken?",
  skill: "Find the swing that holds a trend up (or down) and see whether the last close broke it.",
  needs: { tf: "4h", history: 220, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const n = c.length;
    type Moment = { k: number; read: BrokenRead };
    const broken: Moment[] = [];
    const holds: Moment[] = [];
    for (let k = Math.max(80, n - 150); k < n; k++) {
      const read = trendBrokenRead(c.slice(0, k + 1));
      if (read) (read.broken ? broken : holds).push({ k, read });
    }
    const groups = [broken, holds].filter(g => g.length > 0);
    if (groups.length === 0) return null;
    const { k, read } = rng.pick(rng.pick(groups));
    const shown = c.slice(0, k + 1);
    const last = shown[shown.length - 1]!;
    const up = read.trend === "up";
    const swingName = up ? "higher low" : "lower high";
    const side = up ? "buys" : "sells";
    const outWord = up ? "below" : "above";
    const inWord = up ? "above" : "below";

    // Show the swings the read used: from the older of the last two highs and lows.
    const done = read.all.filter(s => s.confirmed);
    const used = [...done.filter(s => s.kind === "high").slice(-2), ...done.filter(s => s.kind === "low").slice(-2)];
    const firstTime = Math.min(...used.map(s => s.time));
    const firstIdx = Math.max(0, shown.findIndex(x => x.time === firstTime));
    const focus = Math.min(shown.length, Math.max(50, shown.length - firstIdx + 8));
    const answerMarks: ChartMark[] = done
      .filter(s => s.time >= shown[shown.length - focus]!.time)
      .map(s => ({
        time: s.time,
        label: s.label,
        tone: s.time === read.pivot.time ? "brand" : "muted",
        position: s.kind === "high" ? "above" : "below",
      }));

    const options: Option[] = [
      {
        id: "yes",
        label: `Yes: price closed ${outWord} the last ${swingName} — no new ${side}`,
        why: read.broken ? undefined : `The last close (${price(last.close)}) is still ${inWord} the last ${swingName} (${price(read.pivot.price)}).`,
      },
      {
        id: "no",
        label: `No: the ${swingName} still holds`,
        why: read.broken ? `The last close (${price(last.close)}) is ${outWord} the last ${swingName} (${price(read.pivot.price)}): ${up ? "buyers" : "sellers"} failed to hold it.` : undefined,
      },
    ];

    const pivotText = `The last ${swingName} is ${price(read.pivot.price)} (${dateText(read.pivot.time)}).`;
    const explain = read.broken
      ? `${pivotText} The last close, ${price(last.close)}, is ${outWord} it: the run of ${up ? "higher lows" : "lower highs"} has failed, so the ${up ? "up" : "down"}trend is in doubt. No new ${side} until a new structure forms.`
      : `${pivotText} The last close, ${price(last.close)}, is still ${inWord} it, so the ${up ? "up" : "down"}trend's structure holds and ${side} on pullbacks are still allowed.`;

    return {
      kind: "choice",
      prompt: up
        ? "This 4h chart has been making higher highs and higher lows (an uptrend). Has the last close broken it?"
        : "This 4h chart has been making lower highs and lower lows (a downtrend). Has the last close broken it?",
      chart: {
        tf: data.tf,
        candles: shown,
        ahead: after(data, k, 12),
        focus,
        answerLines: [{ price: read.pivot.price, label: up ? "Last higher low" : "Last lower high", tone: "brand", dashed: true }],
        answerMarks,
      },
      options,
      answer: read.broken ? "yes" : "no",
      explain: `${explain} A swing counts once price turns 2.5 ATR (2.5 average candles) away from it.`,
      outcome: moveText(last.close, after(data, k, 12)),
    };
  },
};

// ── 4. Do the timeframes agree? ─────────────────────────────────────────────

export type TfCall = "buys" | "sells" | "nothing";

/**
 * The trend-pullback rule: buys need a bullish 4h bias AND the 1h EMA 20
 * above the EMA 50; sells the mirror; anything else is no trade. Null when
 * the bias score sits within 0.01 of its ±0.2 line or the EMAs are within
 * 0.15 ATR of each other.
 */
export function timeframesCall(bias: Pick<BiasCheck, "direction" | "confidence">, e20: number, e50: number, a: number): TfCall | null {
  if (Math.abs(bias.confidence - 0.2) < 0.01) return null;
  if (!(a > 0) || Math.abs(e20 - e50) < 0.15 * a) return null;
  if (bias.direction === "bullish" && e20 > e50) return "buys";
  if (bias.direction === "bearish" && e20 < e50) return "sells";
  return "nothing";
}

function votesText(b: BiasCheck): string {
  const ema = b.ema.vote === "up" ? "EMAs stacked up" : b.ema.vote === "down" ? "EMAs stacked down" : "EMAs mixed";
  const sw = b.swings.vote === "up" ? "swing highs rising" : b.swings.vote === "down" ? "swing lows falling" : "swings mixed";
  return `${ema}, RSI ${b.rsi.value.toFixed(0)}, ${sw}`;
}

export const timeframesAgree: GameDef = {
  id: "timeframes-agree",
  level: "expert",
  title: "Do the timeframes agree?",
  skill: "Trade a pullback only when the 4h bias and the 1h trend point the same way.",
  needs: { tf: "1h", history: 220, ahead: 12, htf: true },
  make(data) {
    if (!data?.htf) return null;
    const bias = biasCheck(data.htf);
    const e20 = emaLast(data.candles, 20);
    const e50 = emaLast(data.candles, 50);
    const a = atr(data.candles);
    if (!bias || e20 === null || e50 === null || a === null) return null;
    const call = timeframesCall(bias, e20, e50, a);
    if (!call) return null;
    const dir = bias.direction === "bullish" ? "Bullish" : bias.direction === "bearish" ? "Bearish" : "Neutral";
    const oneHourUp = e20 > e50;
    const emaWord = oneHourUp ? "above" : "below";
    const last = data.candles[data.candles.length - 1]!;

    const why = (id: TfCall): string | undefined => {
      if (id === call) return undefined;
      if (id === "buys") return bias.direction !== "bullish" ? `The 4h bias is ${dir.toLowerCase()}, not bullish.` : "On the 1h the EMA 20 is below the EMA 50: the 1h points down.";
      if (id === "sells") return bias.direction !== "bearish" ? `The 4h bias is ${dir.toLowerCase()}, not bearish.` : "On the 1h the EMA 20 is above the EMA 50: the 1h points up.";
      return `They agree: the 4h bias is ${dir.toLowerCase()} and the 1h EMA 20 is ${emaWord} the EMA 50.`;
    };

    const agree = call !== "nothing";
    return {
      kind: "choice",
      prompt: "The desk's 4h bias is above. On this 1h chart, read the EMA 20 (blue) against the EMA 50 (violet). Which trades does the desk allow now?",
      facts: [
        { label: "4h bias", value: dir },
        { label: "4h votes", value: votesText(bias) },
      ],
      chart: chartOf(data, { emas: [20, 50], focus: 80 }),
      options: [
        { id: "buys", label: "Buys only: both point up", why: why("buys") },
        { id: "sells", label: "Sells only: both point down", why: why("sells") },
        { id: "nothing", label: "Nothing: they disagree (or the 4h is neutral)", why: why("nothing") },
      ],
      answer: call,
      explain: `4h: ${dir.toLowerCase()} (${votesText(bias)}). 1h: EMA 20 (${price(e20)}) is ${emaWord} EMA 50 (${price(e50)}), so the 1h trend is ${oneHourUp ? "up" : "down"}. ${
        agree
          ? `Both point the same way: the desk only takes ${call}.`
          : "A trend pullback needs both to agree; when they don't (or the 4h is neutral) the desk takes no trade."
      }`,
      outcome: moveText(last.close, data.ahead),
    };
  },
};

// ── 5. Gap past the stop ────────────────────────────────────────────────────

export const gapPastStop: GameDef = {
  id: "gap-past-stop",
  level: "expert",
  title: "Gap past the stop",
  skill: "Know that a stop fills at the first price the market offers: a gap past it costs more than 1R.",
  make(_data, rng) {
    const p = randomPlan(rng, [0.8, 2.5]);
    const side = sideOf(p.isLong);
    const gapR = rng.float(0.15, 1.2);
    const open = snap(p.stop + (p.isLong ? -1 : 1) * gapR * p.risk);
    // The stop fills at the open (Replay's settle rule for a gap).
    const r = rOf(side, p.entry, p.stop, open);
    if (!(r < -1.1)) return null;
    const verb = p.isLong ? "bought" : "sold";
    const past = p.isLong ? "below" : "above";
    const diff = p.isLong ? `(${price(open)} − ${price(p.entry)})` : `(${price(p.entry)} − ${price(open)})`;
    return {
      kind: "number",
      prompt: `You ${verb} ${p.coin} at ${price(p.entry)} with the stop at ${price(p.stop)}. News hits and the next candle opens at ${price(open)}, ${past} your stop. What is the trade's result in R? (A loss is negative.)`,
      facts: [
        { label: "Side", value: p.isLong ? "Buy" : "Sell" },
        { label: "Entry", value: price(p.entry) },
        { label: "Stop", value: price(p.stop) },
        { label: "Next candle opens at", value: price(open) },
      ],
      answer: r,
      tolerance: 0.05,
      unit: "R",
      decimals: 2,
      explain: `Price jumped over your stop, so it fills at the first price there was: the open, ${price(open)}. Result: ${diff} ÷ ${diffText(p.risk, p.entry)} planned risk per coin = ${rText(r, 2)} (R = the risk you planned). A stop limits risk but doesn't guarantee its price; that's why each trade risks only ${RISK_PCT}% of the account.`,
    };
  },
};

// ── 6. Slippage changes risk ────────────────────────────────────────────────

/** Real risk ÷ planned risk when a market order fills `slipPct` % worse than the planned entry. */
export function slippageRiskR(isLong: boolean, entry: number, stop: number, slipPct: number): number {
  const fill = entry * (1 + (isLong ? 1 : -1) * slipPct / 100);
  return Math.abs(fill - stop) / Math.abs(entry - stop);
}

export const slippageRisk: GameDef = {
  id: "slippage-risk",
  level: "expert",
  title: "Slippage changes risk",
  skill: "See how a worse fill moves the entry away from the stop and raises the real risk.",
  make(_data, rng) {
    const p = randomPlan(rng, [0.5, 2.5]);
    const slip = roundTo(rng.float(0.05, 0.6), 2);
    const answer = slippageRiskR(p.isLong, p.entry, p.stop, slip);
    const fill = p.entry * (1 + (p.isLong ? 1 : -1) * slip / 100);
    const newRisk = Math.abs(fill - p.stop);
    const stopPct = (p.risk / p.entry) * 100;
    return {
      kind: "number",
      prompt: `Price is moving fast, so your market ${p.isLong ? "buy" : "sell"} on ${p.coin} fills ${pctText(slip)} worse than the planned entry. With the same stop, how many R (planned risks) does the trade now risk?`,
      facts: [
        { label: "Side", value: p.isLong ? "Buy" : "Sell" },
        { label: "Planned entry", value: price(p.entry) },
        { label: "Stop", value: price(p.stop) },
        { label: "Fill is worse by", value: pctText(slip) },
      ],
      answer,
      tolerance: 0.02,
      unit: "R",
      decimals: 2,
      explain: `The fill is ${price(p.entry)} ${p.isLong ? "+" : "−"} ${pctText(slip)} = ${price(fill)}. New risk ${diffText(newRisk, p.entry)} ÷ planned risk ${diffText(p.risk, p.entry)} = ${answer.toFixed(2)}R (the stop was ${pctText(stopPct)} away, so ${pctText(slip)} more is ${(slip / stopPct).toFixed(2)}R extra). Chasing a fast move fills further from the stop, so the same position risks more than you planned.`,
    };
  },
};

// ── 7. Good trade or lucky? ─────────────────────────────────────────────────

export interface TradeRules { ready: boolean; sized: boolean; stopKept: boolean }
export type ProcessCall = "good-good" | "good-bad" | "bad-good" | "bad-bad";

/** Decision good only when every rule was followed; result good when it made money. */
export function processCall(rules: TradeRules, r: number): ProcessCall {
  const decision = rules.ready && rules.sized && rules.stopKept ? "good" : "bad";
  return `${decision}-${r > 0 ? "good" : "bad"}` as ProcessCall;
}

const PROCESS_LABELS: Record<ProcessCall, string> = {
  "good-good": "Good decision, good result",
  "good-bad": "Good decision, bad result",
  "bad-good": "Bad decision, good result",
  "bad-bad": "Bad decision, bad result",
};

export const processOrLuck: GameDef = {
  id: "process-or-luck",
  level: "expert",
  title: "Good trade or lucky?",
  skill: "Judge a trade by whether it followed the rules, not by whether it made money.",
  make(_data, rng) {
    const coin = rng.pick(COINS).coin;
    const isLong = rng.chance(0.5);
    const account = rng.pick([1000, 2000, 5000]);
    const rules: TradeRules = { ready: true, sized: true, stopKept: true };
    if (rng.chance(0.5)) {
      const keys = rng.shuffle(["ready", "sized", "stopKept"] as const);
      const howMany = rng.chance(0.7) ? 1 : 2;
      for (const key of keys.slice(0, howMany)) rules[key] = false;
    }
    const win = rng.chance(0.5);
    const riskPct = rules.sized ? RISK_PCT : rng.pick([1.5, 2]);
    const riskUsd = (account * riskPct) / 100;
    const r = win ? roundTo(rng.float(1.2, 2.5), 1) : rules.stopKept ? -1 : -roundTo(rng.float(1.4, 2.2), 1);
    const call = processCall(rules, r);
    const goodDecision = call.startsWith("good");

    const broken: string[] = [];
    if (!rules.ready) broken.push("it wasn't a Ready signal");
    if (!rules.sized) broken.push(`it risked ${riskPct}% instead of ${RISK_PCT}%`);
    if (!rules.stopKept) broken.push("the stop was moved further away");
    const brokenText = broken.join(" and ");

    const why = (id: ProcessCall): string | undefined => {
      if (id === call) return undefined;
      const parts: string[] = [];
      if (id.startsWith("good") && !goodDecision) parts.push(`The decision broke a rule: ${brokenText}.`);
      if (id.startsWith("bad") && goodDecision) parts.push("Every rule was followed, so the decision was good.");
      if (id.endsWith("good") && r <= 0) parts.push(`The result was a loss (${rText(r)}).`);
      if (id.endsWith("bad") && r > 0) parts.push(`The result was a win (${rText(r)}).`);
      return parts.join(" ");
    };

    const decisionText = goodDecision
      ? `Every rule was followed: a Ready signal, ${RISK_PCT}% risk, and the stop left alone, so the decision was good.`
      : `The decision was bad: ${brokenText}.`;
    const resultText = `The result, ${rText(r)} (${usd(r * riskUsd)}), was ${r > 0 ? "good" : "bad"}.`;
    const lesson = goodDecision && r <= 0
      ? "A loss on a by-the-rules trade is just the cost of trading: keep doing it."
      : !goodDecision && r > 0
        ? "A lucky win that broke rules trains the wrong habit."
        : "Judge decisions by the rules, not by one result.";

    return {
      kind: "choice",
      prompt: `Your ${coin} ${isLong ? "buy" : "sell"} just closed. Judge it two ways: was the decision good, and was the result good?`,
      facts: [
        { label: "Signal", value: rules.ready ? "Ready (all checks passed)" : rng.pick(["Almost: one check still missing", "None: it just looked like it would move"]) },
        { label: "Risk", value: `${riskPct}% of ${usd(account, 0)} (${usd(riskUsd)})${rules.sized ? ": the rule" : ""}` },
        { label: "Stop", value: rules.stopKept ? "Left where it was planned" : "Moved further away when price came near" },
        { label: "Result", value: `${rText(r)} (${usd(r * riskUsd)})` },
      ],
      options: (Object.keys(PROCESS_LABELS) as ProcessCall[]).map(id => ({ id, label: PROCESS_LABELS[id], why: why(id) })),
      answer: call,
      explain: `${decisionText} ${resultText} ${lesson}`,
    };
  },
};

// ── 8. After two losses ─────────────────────────────────────────────────────

export type DayCall = "stop" | "take" | "wait";
export type DeskNow = "ready" | "almost" | "nothing";

/** The day's rule: over after 3 trades or −3R; else take a Ready signal at the normal size; else wait. */
export function dayCall(tradesToday: number, rToday: number, desk: DeskNow): DayCall {
  if (tradesToday >= MAX_TRADES_PER_DAY || rToday <= -MAX_DAILY_LOSS_R) return "stop";
  return desk === "ready" ? "take" : "wait";
}

export const afterTwoLosses: GameDef = {
  id: "after-two-losses",
  level: "expert",
  title: "After two losses",
  skill: "Follow the day's limits after losses: no revenge trades, no bigger size, and only Ready signals.",
  make(_data, rng) {
    const target = rng.pick(["stop", "take", "wait"] as const);
    const coin = rng.pick(COINS).coin;
    const side = rng.chance(0.5) ? "BUY" : "SELL";
    // The last two trades lost; how many of them (and what else) fell today decides the day.
    let today: number[];
    let yesterday: number[] = [];
    if (target === "stop") {
      today = rng.chance(0.5)
        ? [rng.chance(0.5) ? roundTo(rng.float(1.2, 2), 1) : -1, -1, -1]
        : [-1, -roundTo(rng.float(2.2, 2.6), 1)];
    } else {
      const n = rng.pick([0, 1, 2]);
      today = [-1, -1].slice(0, n);
      yesterday = [-1, -1].slice(n);
    }
    const desk: DeskNow = target === "take" ? "ready" : target === "wait" ? rng.pick(["almost", "nothing"] as const) : rng.pick(["ready", "ready", "almost", "nothing"] as const);
    const rToday = roundTo(today.reduce((s, x) => s + x, 0), 1);
    const call = dayCall(today.length, rToday, desk);

    const lastTwoText = yesterday.length === 2
      ? `${rText(-1)}, ${rText(-1)} (both yesterday)`
      : yesterday.length === 1
        ? `${rText(-1)} yesterday, ${rText(today[0]!)} today`
        : `${today.slice(-2).map(x => rText(x)).join(", ")} (both today)`;
    const deskText = desk === "ready" ? `${coin}: Ready ${side} signal` : desk === "almost" ? `${coin}: Almost (one check missing)` : "Nothing Ready or close";
    const dayOverWhy = today.length >= MAX_TRADES_PER_DAY
      ? `${today.length} trades is the day's limit`
      : `${rText(rToday)} is past the ${MAX_DAILY_LOSS_R}R daily loss limit`;
    const openText = `${today.length} of ${MAX_TRADES_PER_DAY} trades, ${rText(rToday)}; it stops at ${MAX_TRADES_PER_DAY} trades or −${MAX_DAILY_LOSS_R}R`;

    const why = (id: DayCall | "double"): string | undefined => {
      if (id === call) return undefined;
      if (id === "double") return `Doubling to win it back is revenge trading: one more loss would cost 2R at once. Every trade risks the same ${RISK_PCT}%.`;
      if (id === "stop") return `The day isn't over (${openText}).${today.length === 0 ? " Yesterday's losses don't count today." : " Two losses alone don't end it."}`;
      if (id === "take") return call === "stop" ? `The day is over (${dayOverWhy}): no more trades today, even a Ready one.` : `There's no Ready signal to take: ${desk === "almost" ? "an Almost setup still has a check missing" : "nothing is set up"}.`;
      return call === "stop" ? `Not just wait: the day is over (${dayOverWhy}). Close the desk until tomorrow.` : "There is a Ready signal and the day is open: the rules say take it at the normal size.";
    };

    const explain =
      call === "stop"
        ? `The day ends after ${MAX_TRADES_PER_DAY} trades or once it's down ${MAX_DAILY_LOSS_R}R. Here ${dayOverWhy}, so the desk is closed until tomorrow, whatever is on the screen. The urge to win it back is exactly why the limit exists.`
        : call === "take"
          ? `The day is still open (${openText}) and there's a Ready signal. Take it at the normal ${RISK_PCT}% size: the losses before don't change the next trade's odds, so they don't change its size either.`
          : `The day is still open (${openText}), but there's no Ready signal. Wait: wanting to win it back is not a setup.`;

    return {
      kind: "choice",
      prompt: "Your last two trades lost and you want to win it back. By the desk's rules, what do you do now?",
      facts: [
        { label: "Your last two trades", value: lastTwoText },
        { label: "Trades today", value: `${today.length} of ${MAX_TRADES_PER_DAY}` },
        { label: "Today so far", value: rText(rToday) },
        { label: "On the desk now", value: deskText },
        { label: "How you feel", value: "You want to win it back" },
      ],
      options: [
        { id: "stop", label: "Stop for today", why: why("stop") },
        { id: "take", label: "Take it at the normal size", why: why("take") },
        { id: "wait", label: "Wait: no Ready signal", why: why("wait") },
        { id: "double", label: "Double the size to win it back", why: why("double") },
      ],
      answer: call,
      explain,
    };
  },
};

// ── 9. Ready or still forming? ──────────────────────────────────────────────

/** The words for the trigger the last candle makes for this setup (rules.ts), or null when it isn't one. */
export function triggerOnLast(candles: Candle[], isLong: boolean, archetype: string | null): string | null {
  const last = candles[candles.length - 1];
  const prev = candles[candles.length - 2];
  if (!last || !prev) return null;
  if (archetype === "range_fade") {
    return isRejectionWick(last, isLong) ? `a long ${isLong ? "lower" : "upper"} wick, more than ${WICK_RATIO}× the body` : null;
  }
  const t = reactionTrigger(prev, last, isLong);
  return t ? triggerWords(t, isLong) : null;
}

/** Too close to tell by eye: a wick ratio near 1.2 (range fade) or a close a hair from the last candle's high/low. */
function nearTrigger(candles: Candle[], isLong: boolean, archetype: string | null): boolean {
  const last = candles[candles.length - 1]!;
  const prev = candles[candles.length - 2]!;
  if (archetype === "range_fade") return Math.abs(wickRatio(last, isLong) - WICK_RATIO) < 0.1;
  const a = atr(candles) ?? 0;
  return Math.abs(last.close - (isLong ? prev.high : prev.low)) < 0.05 * a;
}

function triggerList(isLong: boolean, archetype: string | null): string {
  if (archetype === "range_fade") return `a candle at the edge with a long ${isLong ? "lower" : "upper"} wick (more than ${WICK_RATIO}× its body)`;
  return isLong
    ? `${TRIGGER_WORDS.bullish_engulfing}, ${TRIGGER_WORDS.hammer}, or ${TRIGGER_WORDS.level_reclaim}`
    : `${TRIGGER_WORDS.bearish_engulfing}, ${TRIGGER_WORDS.shooting_star}, or a close below the last candle's low`;
}

const SIGNAL_HISTORY = 200;

export const setupForming: GameDef = {
  id: "setup-forming",
  level: "expert",
  title: "Ready or still forming?",
  skill: "Tell a finished signal from a setup still forming: no entry until the trigger candle closes.",
  needs: { tf: "1h", history: SIGNAL_HISTORY },
  signals: true,
  make(data, rng) {
    const sigs = data?.signals ?? [];
    if (!data || sigs.length === 0) return null;
    const s = rng.pick(sigs);
    const isLong = s.action === "LONG";
    const BUY = isLong ? "BUY" : "SELL";
    const now = rng.chance(0.5);
    let hoursBefore = 0;
    if (!now) {
      const ok: number[] = [];
      for (const h of [1, 2, 3]) {
        const t = s.signalTime - h * 3600;
        if (sigs.some(x => x.signalTime === t)) continue;
        const { shown } = cutAt(data.candles, t, 3600, SIGNAL_HISTORY);
        if (shown.length < 60 || nearTrigger(shown, isLong, s.archetype)) continue;
        if (triggerOnLast(shown, isLong, s.archetype)) continue;
        ok.push(h);
      }
      if (ok.length === 0) return null;
      hoursBefore = rng.pick(ok);
    }
    const nowSec = s.signalTime - hoursBefore * 3600;
    const { shown, ahead } = cutAt(data.candles, nowSec, 3600, SIGNAL_HISTORY, 24);
    if (shown.length < 60 || nearTrigger(shown, isLong, s.archetype)) return null;
    const words = triggerOnLast(shown, isLong, s.archetype);
    if (now) {
      // The candle must read as the trigger, and a trend setup's EMAs must point its way.
      if (!words) return null;
      if (s.archetype !== "range_fade") {
        const e20 = emaLast(shown, 20);
        const e50 = emaLast(shown, 50);
        if (e20 === null || e50 === null || (isLong ? e20 <= e50 : e20 >= e50)) return null;
      }
    }
    const answer = now ? BUY : "wait";
    const setup = setupName(s.archetype);
    const last = shown[shown.length - 1]!;
    const triggerCandle = data.candles.find(k => k.time + 3600 === s.signalTime);
    const trendNote = s.archetype === "range_fade" ? "" : ` with the EMA 20 ${isLong ? "above" : "below"} the EMA 50`;

    const options: Option[] = [
      {
        id: "BUY",
        label: "BUY now",
        why: answer === "BUY" ? undefined : now ? "This setup points down: the trigger is a sell trigger." : "No trigger candle has closed yet: entering now is guessing the setup will finish.",
      },
      {
        id: "SELL",
        label: "SELL now",
        why: answer === "SELL" ? undefined : now ? "This setup points up: the trigger is a buy trigger." : "No trigger candle has closed yet: entering now is guessing the setup will finish.",
      },
      {
        id: "wait",
        label: "Not yet: still forming",
        why: answer === "wait" ? undefined : `The trigger has closed (${words}). Waiting longer means chasing a worse price.`,
      },
    ];

    return {
      kind: "choice",
      prompt: `The engine is watching a ${setup} on ${data.coin}, and the last 1h candle has just closed. Is there a signal now?`,
      facts: [
        { label: "Coin", value: data.coin },
        { label: "Setup", value: setup[0]!.toUpperCase() + setup.slice(1) },
        { label: "Now", value: dateText(nowSec) },
      ],
      chart: {
        tf: "1h",
        candles: shown,
        ahead,
        emas: [20, 50],
        focus: 60,
        answerMarks: triggerCandle ? [{ time: triggerCandle.time, label: "T", tone: "brand", position: isLong ? "below" : "above" }] : undefined,
      },
      options,
      answer,
      explain: now
        ? `The last candle is the ${setup} trigger: ${words}${trendNote}. The engine sent a ${BUY} at its close: entry ${price(s.entry)}, stop ${price(s.stopLoss)}.`
        : `Not yet. A ${setup} needs its trigger candle to close first: ${triggerList(isLong, s.archetype)}. The last candle (${price(last.close)}) isn't one, so the setup is still forming: no entry until the trigger closes.`,
      outcome: now
        ? signalResultText(s)
        : `The engine's ${BUY} fired ${hoursText(hoursBefore)} later. ${signalResultText(s)}`,
    };
  },
};

// ── 10. The engine's target 1 ───────────────────────────────────────────────

export const signalTarget: GameDef = {
  id: "signal-target",
  level: "expert",
  title: "The engine's target 1",
  skill: "Place target 1 the engine's way: the nearest level in the way if it's far enough, else 1.5R.",
  needs: { tf: "1h", history: SIGNAL_HISTORY },
  signals: true,
  make(data, rng) {
    const sigs = data?.signals ?? [];
    if (!data || sigs.length === 0) return null;
    const s = rng.pick(sigs);
    const isLong = s.action === "LONG";
    const dir = isLong ? 1 : -1;
    const risk = Math.abs(s.entry - s.stopLoss);
    if (!(risk > 0) || dir * (s.tp1 - s.entry) <= 0 || dir * (s.entry - s.stopLoss) <= 0) return null;
    const { shown, ahead } = cutAt(data.candles, s.signalTime, 3600, SIGNAL_HISTORY, 24);
    if (shown.length < 60) return null;
    const lo = Math.min(...shown.map(k => k.low));
    const hi = Math.max(...shown.map(k => k.high));
    if (s.tp1 < lo - (hi - lo) * 0.4 || s.tp1 > hi + (hi - lo) * 0.4) return null;
    const tp1R = Math.abs(s.tp1 - s.entry) / risk;
    const BUY = isLong ? "BUY" : "SELL";
    const levelWord = isLong ? "resistance" : "support";

    let explain: string;
    if (s.archetype === "range_fade") {
      explain = `For a range fade, target 1 is the middle of the range: ${price(s.tp1)} (${tp1R.toFixed(1)}R away). Target 2 sits just inside the far edge (${price(s.tp2)}).`;
    } else {
      const rule = `Target 1 is the nearest ${levelWord} in the way if it's more than ${MIN_ROOM_R}R away, otherwise ${TP1_FALLBACK_R}R.`;
      explain = Math.abs(tp1R - TP1_FALLBACK_R) < 0.02
        ? `${rule} Here it's the ${TP1_FALLBACK_R}R fallback: ${price(s.entry)} ${isLong ? "+" : "−"} ${TP1_FALLBACK_R} × ${diffText(risk, s.entry)} = ${price(s.tp1)}. Target 2 is ${TP2_R}R: ${price(s.tp2)}.`
        : `${rule} Here it's the ${levelWord} at ${price(s.tp1)}, ${tp1R.toFixed(1)}R away. Target 2 is ${TP2_R}R: ${price(s.tp2)}.`;
    }

    return {
      kind: "price",
      prompt: `The engine just sent this ${BUY} on ${data.coin} (entry and stop drawn). Click where it puts target 1.`,
      facts: [
        { label: "Setup", value: s.archetype ? setupName(s.archetype)[0]!.toUpperCase() + setupName(s.archetype).slice(1) : "Engine signal" },
        { label: "Entry", value: price(s.entry) },
        { label: "Stop", value: price(s.stopLoss) },
        { label: "Risk (1R)", value: diffText(risk, s.entry) },
      ],
      chart: {
        tf: "1h",
        candles: shown,
        ahead,
        emas: [20, 50],
        focus: 80,
        lines: [
          { price: s.entry, label: "Entry", tone: "brand" },
          { price: s.stopLoss, label: "Stop", tone: "loss" },
        ],
        answerLines: [
          { price: s.tp1, label: "Target 1", tone: "profit" },
          { price: s.tp2, label: "Target 2", tone: "profit", dashed: true },
        ],
      },
      answer: s.tp1,
      accept: { from: s.tp1 - 0.25 * risk, to: s.tp1 + 0.25 * risk },
      explain: `${explain} Anywhere within a quarter of the risk (${diffText(0.25 * risk, s.entry)}) of it counts.`,
      outcome: signalResultText(s),
    };
  },
};

export const GAMES: GameDef[] = [
  holdOrFold,
  rangeFloorWick,
  trendBroken,
  timeframesAgree,
  gapPastStop,
  slippageRisk,
  processOrLuck,
  afterTwoLosses,
  setupForming,
  signalTarget,
];
